/**
 * API utilities for making authenticated requests to the backend
 */

/**
 * Creates authorization headers for API requests
 */
export const authHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

/**
 * Unwraps Lambda streaming response format.
 * Lambda responses may have a body property that's a JSON string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unwrapLambdaResponse<T>(raw: any): T {
  if (typeof raw.body === 'string') {
    return JSON.parse(raw.body) as T;
  }
  return raw as T;
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    message?: string
  ) {
    super(message ?? `Request failed: ${status}`);
    this.name = 'HttpError';
  }
}

// Structural check instead of instanceof: dev bundlers can duplicate the
// module across chunks, making instanceof fail across the copies.
export function httpErrorStatus(error: unknown): number | null {
  if (error instanceof Error && error.name === 'HttpError') {
    const status = (error as unknown as { status?: unknown }).status;
    if (typeof status === 'number') {
      return status;
    }
  }
  return null;
}

/**
 * React Query retry rule for loading a document: a missing document or
 * missing access won't change by asking again, so only network trouble and
 * server errors are retried.
 */
export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  const status = httpErrorStatus(error);
  if (status !== null && status >= 400 && status < 500) return false;
  return failureCount < 3;
}
